/**
 * ChatLauncher — bottom-right FAB with unread badge (issue #351).
 *
 * Mounted via ``ChatProvider`` so every authenticated page carries it.
 * Click toggles the slide-in ``ChatPanel``.
 */
import React from 'react';
import { MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './ChatLauncher.css';

export interface ChatLauncherProps {
  isOpen: boolean;
  unreadCount: number;
  onClick: () => void;
}

export const ChatLauncher: React.FC<ChatLauncherProps> = ({
  isOpen,
  unreadCount,
  onClick,
}) => {
  const { t } = useTranslation();
  const badge = unreadCount > 0 ? unreadCount : null;

  return (
    <button
      type="button"
      className={[
        'chat-launcher',
        isOpen ? 'chat-launcher--open' : '',
        badge !== null ? 'chat-launcher--has-unread' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={t('chat.launcher.aria', 'Open chat')}
      aria-expanded={isOpen}
      onClick={onClick}
    >
      <MessageCircle size={24} aria-hidden />
      {badge !== null && (
        <span
          className="chat-launcher__badge"
          aria-label={t('chat.launcher.unreadBadgeAria', '{{count}} unread messages', { count: badge })}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
};
